import { Link } from "react-router-dom";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { HardHat, Smartphone, Share, PlusSquare, ShieldCheck, Wifi, Bell, Play } from "lucide-react";
import { SEO } from "@/lib/seo";
import { PublicFooter } from "./FAQ";

const APK_URL = "/downloads/fieldlink.apk";
const PLAY_URL = "https://play.google.com/store/apps/details?id=nz.co.fieldlink.app";

export default function DownloadPage() {
  return (
    <div className="bg-[#f6f5f1] text-[#142635] min-h-screen">
      <SEO
        title="Get the Fieldlink App — Google Play (Android) & iPhone Install | Fieldlink NZ"
        description="Install Fieldlink on your phone. Get it on Google Play for Android, or a 30-second home-screen install for iPhone. Works offline on site."
        path="/download"
      />

      <MarketingHeader />

      <section className="bg-[#f6f5f1] text-[#142635] border-b border-[#cbd3d5] py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="block text-[11px] font-mono font-bold uppercase tracking-[0.28em] text-[#a63c0b] mb-4">Now on Google Play</div>
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl leading-none mb-4">
            Get Fieldlink on<br /><span className="text-[#a63c0b]">every phone in the crew</span>
          </h1>
          <p className="text-[#465562] max-w-xl mx-auto">
            Install Fieldlink from the Google Play Store on Android, or add it to your home screen on iPhone in 30 seconds. Full screen, your own icon, works offline on remote sites.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-14 grid md:grid-cols-2 gap-8">
        {/* Android */}
        <div className="rounded-lg border border-[#cbd3d5] bg-white p-7" data-testid="download-android-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-md bg-[#3DDC84] flex items-center justify-center"><Smartphone className="w-5 h-5 text-black" strokeWidth={2.5} /></div>
            <h2 className="font-display font-black uppercase tracking-tight text-2xl">Android</h2>
          </div>
          <p className="text-sm text-[#52616c] leading-relaxed mb-5">
            Fieldlink is on the Google Play Store — install it like any other app and it keeps itself up to date.
          </p>
          <a
            href={PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider px-6 py-3.5 w-full justify-center"
            data-testid="download-play-btn"
          >
            <Play className="w-5 h-5" fill="currentColor" /> Get it on Google Play
          </a>
          <ol className="mt-6 space-y-3 text-sm text-[#465562]">
            {[
              "Tap the button above — it opens Fieldlink on Google Play",
              "Tap Install and wait for it to finish",
              "Open Fieldlink and sign in — it's on your home screen to stay",
            ].map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-6 h-6 shrink-0 rounded-sm bg-[#FF5F15] text-[#142635] text-xs font-black flex items-center justify-center">{i + 1}</span>
                <span className="leading-snug">{s}</span>
              </li>
            ))}
          </ol>
          <p className="text-[11px] text-[#52616c] mt-4 leading-relaxed">
            No Google Play on your device? <a href={APK_URL} download="fieldlink.apk" className="underline font-semibold" data-testid="download-apk-btn">Download the APK directly</a> and allow &quot;install unknown apps&quot; when asked.
          </p>
        </div>

        {/* iPhone */}
        <div className="rounded-lg border border-[#cbd3d5] bg-white p-7" data-testid="download-ios-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-md bg-white border border-[#cbd3d5] flex items-center justify-center"><Smartphone className="w-5 h-5 text-[#142635]" strokeWidth={2.5} /></div>
            <h2 className="font-display font-black uppercase tracking-tight text-2xl">iPhone &amp; iPad</h2>
          </div>
          <p className="text-sm text-[#52616c] leading-relaxed mb-5">
            Apple doesn&apos;t allow app installs outside the App Store — but Fieldlink installs to your home screen in 30 seconds and works just like a native app.
          </p>
          <ol className="space-y-3 text-sm text-[#465562]">
            {[
              ["Open fieldlink.co.nz in Safari", "It must be Safari — Chrome on iPhone can't install apps"],
              ["Tap the Share button", "The square with the arrow, bottom-middle of the screen"],
              ["Tap \"Add to Home Screen\"", "Scroll down the share sheet if you don't see it"],
              ["Tap Add", "Fieldlink appears on your home screen with its own icon"],
            ].map(([s, sub], i) => (
              <li key={i} className="flex gap-3">
                <span className="w-6 h-6 shrink-0 rounded-sm bg-[#FF5F15] text-[#142635] text-xs font-black flex items-center justify-center">{i + 1}</span>
                <span className="leading-snug">
                  <b>{s}</b>
                  <span className="block text-[#52616c] text-xs mt-0.5">{sub}</span>
                </span>
              </li>
            ))}
          </ol>
          <div className="mt-5 flex items-center gap-2 text-xs text-[#52616c]">
            <Share className="w-4 h-4" /> → <PlusSquare className="w-4 h-4" /> → Done. Full screen, offline, push-ready.
          </div>
        </div>
      </section>

      <section className="bg-[#e9eeec] border-y border-[#cbd3d5] py-12">
        <div className="max-w-5xl mx-auto px-6 md:px-12 grid sm:grid-cols-3 gap-6">
          {[
            { icon: Wifi, t: "Plan for connectivity", b: "Offline availability varies by workflow. Plan for a connection to upload evidence and submit plant pre-start records." },
            { icon: Bell, t: "Push notifications", b: "Dispatch changes, timesheet approvals and safety alerts land on the lock screen." },
            { icon: ShieldCheck, t: "Always up to date", b: "The app loads the live Fieldlink — no waiting for store updates, everyone's always on the latest version." },
          ].map((f) => (
            <div key={f.t} className="text-center">
              <f.icon className="w-7 h-7 text-[#a63c0b] mx-auto mb-2" />
              <div className="font-display font-bold uppercase tracking-tight">{f.t}</div>
              <p className="text-xs text-[#52616c] mt-1 leading-relaxed">{f.b}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#e9eeec] border-y border-[#cbd3d5] text-[#142635] text-center py-16">
        <h2 className="font-display font-black uppercase tracking-tight text-3xl mb-3">New to Fieldlink?</h2>
        <p className="text-[#465562] mb-6">Set up your company first, then get the crew on the app.</p>
        <Link to="/register" className="inline-block rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider px-8 py-4">Start free trial →</Link>
      </section>

      <PublicFooter />
    </div>
  );
}
